import React, { ReactNode, useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  goTo,
  nextStep,
  finalStep,
} from '../../../../../redux/actions/merchants/products/purchases';
import PurchaseStep, {
  StepItems,
} from '../../../../../molecules/merchant/products/purchases/PurchaseStep';
import Completion from '../../../../../molecules/merchant/products/purchases/Completion';
import Confirmation from '../Confirmation';
import Customer from '../Customer';
import CompletionFailed from '../../../../../molecules/merchant/products/purchases/CompletionFailed';
import Payment from '../Payment';
import Subscribed from '../../../../../molecules/merchant/products/purchases/Subscribed';
import { ReduxState } from '../../../../../redux/reducers';
import { getPurchases } from '../../../../../redux/selectors/merchants/products/purchases';
import ProductPurchaseAuth from '../ProductPurchaseAuth';
import { useAppDispatch } from '../../../../../redux';

type SettingParams = {
  displayProgress: boolean;
  currentProgress: StepItems | null;
  renderFn: () => ReactNode;
};

const FULL_WORKFLOW_STEPS = [
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];
const SKIP_CUSTOMER_WORKFLOW_STEPS = [
  StepItems.Payment,
  StepItems.Confirmation,
];

const RETURN_TARGET_STEP = 97;
const RESTART_STEP = 98;
const SUBSCRIBED_STEP = 99;

type Props = {
  isEmailVerified: boolean;
  isLoggedIn: boolean;
};
const SchemeTypeSubscription: React.FC<Props> = ({
  isEmailVerified,
  isLoggedIn,
}) => {
  const dispatch = useAppDispatch();

  const {
    merchantProduct,
    firstCharge,
    lastOrder,
    publicId,
    postable,
    settings,
    step,
  } = useSelector(
    (state: ReduxState) => getPurchases(state.merchantsProductsPurchases),
    shallowEqual,
  );

  const renderAuthAt = useCallback(
    (step: number) => {
      if (settings === null) return null;
      return (
        <ProductPurchaseAuth
          schemeType={settings.schemeType}
          postable={postable}
          moveToNext={() => dispatch(nextStep(step))}
          onSubmitSubscribed={() => dispatch(goTo(SUBSCRIBED_STEP))}
          isEmailVerified={isEmailVerified}
          isLoggedIn={isLoggedIn}
        />
      );
    },
    [settings, postable, dispatch, isEmailVerified, isLoggedIn],
  );

  const renderCustomerAt = useCallback(
    (step: number) => {
      if (settings === null) return null;
      return (
        <Customer
          schemeType={settings.schemeType}
          settings={settings.customer}
          postable={postable}
          onSubmit={() => dispatch(nextStep(step))}
        />
      );
    },
    [settings, postable, dispatch],
  );

  const renderPaymentAt = useCallback(
    (step: number) => {
      if (!settings || !settings.payment) return null;
      return (
        <Payment
          schemeType={settings.schemeType}
          settings={settings.payment}
          merchantPublicId={publicId}
          postable={postable}
          onSubmit={() => dispatch(nextStep(step))}
        />
      );
    },
    [settings, postable, dispatch, publicId],
  );

  const renderConfirmationAt = useCallback(
    (step: number) => {
      if (!settings || !merchantProduct || !firstCharge) return null;
      return (
        <Confirmation
          schemeType={settings.schemeType}
          product={merchantProduct}
          firstCharge={firstCharge}
          settings={settings.confirmation}
          postable={postable}
          onSubmit={() => dispatch(finalStep(step))}
          onRestart={() => dispatch(goTo(RESTART_STEP))}
          onReturnTargetStep={() => dispatch(goTo(RETURN_TARGET_STEP))}
          publicId={publicId}
        />
      );
    },
    [settings, merchantProduct, firstCharge, publicId, postable, dispatch],
  );

  const renderCompletion = useCallback(() => {
    if (!settings || !merchantProduct) return null;
    return (
      <Completion
        schemeType={settings.schemeType}
        settings={settings.completion}
        product={merchantProduct}
        firstCharge={firstCharge}
        lastOrder={lastOrder}
        publicId={publicId}
      />
    );
  }, [settings, merchantProduct, firstCharge, lastOrder, publicId]);

  const renderSubscribed = useCallback(() => {
    if (settings === null) return null;
    return <Subscribed settings={settings.completion} />;
  }, [settings]);

  const renderFailedRestart = useCallback(() => {
    if (settings === null) return null;
    const backTo = {
      label: 'purchases.schemeTypeSubscription.restart',
      modifyPage: () => location.reload(),
    };
    return (
      <CompletionFailed
        schemeType={settings.schemeType}
        settings={settings.completion!}
        backTo={backTo}
      />
    );
  }, [settings]);

  const renderFailedReturnTargetStep = useCallback(
    (modifierStep: number) => {
      if (settings === null) return null;
      const backTo = {
        label: 'purchases.schemeTypeSubscription.returnTargetStep',
        modifyPage: () => dispatch(goTo(modifierStep)),
      };
      return (
        <CompletionFailed
          schemeType={settings.schemeType}
          settings={settings.completion!}
          backTo={backTo}
        />
      );
    },
    [settings, dispatch],
  );

  const fullWorkFlowSetting = useCallback((): [
    Array<StepItems>,
    SettingParams | undefined,
  ] => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderAuthAt(0)            },
      1: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderCustomerAt(1)        },
      2: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => renderPaymentAt(2)         },
      3: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderConfirmationAt(3)    },
      4: { displayProgress: false, currentProgress: null,                   renderFn: () => renderCompletion()         },

      97: { displayProgress: false, currentProgress: null,                  renderFn: () => renderFailedReturnTargetStep(1) },
      98: { displayProgress: false, currentProgress: null,                  renderFn: () => renderFailedRestart()           },
      99: { displayProgress: false, currentProgress: null,                  renderFn: () => renderSubscribed()              },
    }[step];

    return [FULL_WORKFLOW_STEPS, setting];
  }, [
    step,
    renderAuthAt,
    renderCustomerAt,
    renderPaymentAt,
    renderConfirmationAt,
    renderCompletion,
    renderFailedRestart,
    renderFailedReturnTargetStep,
    renderSubscribed,
  ]);

  const skipCustomerFlowSetting = useCallback((): [
    Array<StepItems>,
    SettingParams | undefined,
  ] => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderAuthAt(0)            },
      1: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => renderPaymentAt(1)         },
      2: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderConfirmationAt(2)    },
      3: { displayProgress: false, currentProgress: null,                   renderFn: () => renderCompletion()         },

      97: { displayProgress: false, currentProgress: null,                  renderFn: () => renderFailedReturnTargetStep(1) },
      98: { displayProgress: false, currentProgress: null,                  renderFn: () => renderFailedRestart()           },
      99: { displayProgress: false, currentProgress: null,                  renderFn: () => renderSubscribed()              },
    }[step];

    return [SKIP_CUSTOMER_WORKFLOW_STEPS, setting];
  }, [
    step,
    renderAuthAt,
    renderPaymentAt,
    renderConfirmationAt,
    renderCompletion,
    renderFailedReturnTargetStep,
    renderFailedRestart,
    renderSubscribed,
  ]);

  const [allSteps, setting] = useMemo(() => {
    return settings !== null && settings.customer.customerFields!.length !== 0
      ? fullWorkFlowSetting()
      : skipCustomerFlowSetting();
  }, [settings, fullWorkFlowSetting, skipCustomerFlowSetting]);

  return (
    <>
      {setting && setting.currentProgress && setting.displayProgress && (
        <PurchaseStep
          allSteps={allSteps}
          current={allSteps.indexOf(setting.currentProgress) + 1}
        />
      )}
      {setting && setting.renderFn()}
    </>
  );
};

export default SchemeTypeSubscription;
